import React, { useEffect } from 'react'

import { createAndSanitizeMarkup } from '../../../../helper/sanitizer'

import Message from '../../../../models/Message'
import { ReactComponent as PrintIcon } from 'images/Icon_Print.svg'

import { getReadableDate } from '../../../../helper/date'
import { Button } from '../../../Button/Button'

import { useTranslation } from 'react-i18next'

// Tealium
import { Categories } from 'constants/analytics'
import { getUserLoggedInState } from '../../../../redux/reducers/users'
import { useSelector } from 'react-redux'

import events, { Event } from 'pubsub/events'

export interface MessagePaneProps {
  messagePayload?: Message
}

export const MessagePane: React.FC<MessagePaneProps> = ({
  messagePayload
}) => {
  const { t, i18n } = useTranslation('messageCenter')
  const language = i18n.language
  const readableDate = getReadableDate(messagePayload?.createdOn, language)
  const isLoggedIn = useSelector(getUserLoggedInState)

  useEffect(() => {
    const body = document.querySelector('body')
    if (body) {
      body.classList.add('hide-on-print')
    }
    return () => {
      if (body) {
        body.classList.remove('hide-on-print')
      }
    }
  }, [])

  // Print button should be hidden on Safari. (The button's not working)
  let shouldShowPrintButton = true
  try {
    const isChrome = navigator.userAgent.indexOf('Chrome') > -1
    const isSafari = navigator.userAgent.indexOf('Safari') > -1 && !isChrome

    shouldShowPrintButton = !isSafari
  } catch (err) {
    // Do nothing if there's an error
  }

  const handlePrint = () => {
    const messageSubject = messagePayload ? messagePayload?.subject : ''
    const text = 'print'

    const clickEvent: Event = {
      authenticated: isLoggedIn,
      text: text,
      category: Categories.Messages,
      element: 'button',
      messageSubject: messageSubject,
      messageStatus: '',
      eventName: 'message_center_message_modal_click',
      messageComponent: 'message modal',
      activity: 'Link Click',
      url: window.location.href,
      language: i18n.language,
      title: document.title,
      sendToTealium: true,
      sendToCoreAPI: true,
      isMessageCenter: true,
      core: {
        messageId: messagePayload?.guid,
        profileId: messagePayload?.profileId
      }
    }
    events.publish(clickEvent)

    window.print()
  }

  const handleLinkClick = (e: any) => {
    const messageSubject = messagePayload ? messagePayload?.subject : ''
    const text = e.target.text
    const isButton = e.target.className.includes('button')
    const element = isButton ? 'button' : 'link'

    const clickEvent: Event = {
      authenticated: isLoggedIn,
      text: text,
      category: Categories.Messages,
      element: element,
      messageSubject: messageSubject,
      messageStatus: '',
      eventName: 'message_center_message_modal_click',
      messageComponent: 'message modal',
      activity: 'Link Click',
      url: window.location.href,
      language: i18n.language,
      title: document.title,
      sendToTealium: true,
      sendToCoreAPI: true,
      isMessageCenter: true,
      core: {
        messageId: messagePayload?.guid,
        profileId: messagePayload?.profileId
      }
    }
    events.publish(clickEvent)
  }

  const handleRendererRef = (ref: any) => {
    ref?.querySelectorAll('a').forEach((a: any) => { a.onclick = handleLinkClick })
  }

  useEffect(() => {
    const renderEvent: Event = {
      authenticated: isLoggedIn,
      activity: 'Message Render',
      component: 'Header',
      url: window.location.href,
      language: i18n.language,
      title: document.title,
      messageId: messagePayload?.guid,
      profileId: messagePayload?.profileId,
      sendToCoreAPI: true,
      sendToTealium: false,
      isMessageCenter: true,
      category: 'MAX',
      text: ''
    }
    events.publish(renderEvent)
  }, [messagePayload, i18n.language, isLoggedIn])

  return (
    <div
      role='region'
      className='ch-c-dialog__content ch-c-message-pane'
      data-testid='message-pane'
    >
      <div
        className='message-date'
      >
        {t('Sent')}: {readableDate}
      </div>
      {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions, jsx-a11y/click-events-have-key-events */}
      {shouldShowPrintButton ? <Button className='print-label' data-testid='print-btn' onClick={handlePrint}><PrintIcon />{t('Print')}</Button> : null}
      <div
        data-test-id='inner-message-anchor'
        ref={handleRendererRef}
        className='ch-c-render-html qm-block-pii'
        dangerouslySetInnerHTML={createAndSanitizeMarkup(messagePayload?.body)}
      />
    </div>
  )
}
